import clsx from 'clsx';
import { HTMLProps, useEffect, useState } from 'react';
import ReactSlider from 'react-slider';

import NumberInput from '../input/numberInput';
import Typography from '../typography';

interface IProps {
  disabled?: boolean;
  defaultValue?: number;
  minVal?: number;
  maxVal?: number;
  showNumberInput?: boolean;
  handleChange: (value: number) => void;
  liveUpdateValue?: boolean;
}

const defaultStyles = 'bg-gray-100';
const activeStyles = 'group-active:bg-green-50';
const focusStyles = 'focus:bg-green-50';

const renderThumb: (
  props: HTMLProps<HTMLDivElement>,
  state: {
    index: number;
    value: any;
    valueNow: number;
  }
) => JSX.Element = (props, state) => {
  return (
    <div
      {...props}
      key={props.key}
      className={clsx(
        `slider-thumb h-4 w-4 rounded-full ${defaultStyles}`,
        'transition-transform group-hover:scale-125',
        'outline-none focus:outline focus:outline-blue-60',
        activeStyles,
        focusStyles
      )}
      data-testid={`slider-thumb-${state.index}`}
    />
  );
};
const renderTrack: (
  props: HTMLProps<HTMLDivElement>,
  state: {
    index: number;
    value: any;
    valueNow: number;
  }
) => JSX.Element = (props) => {
  return (
    <div
      {...props}
      key={props.key}
      className={clsx(
        'h-1',
        props.key === 'track-0'
          ? `${defaultStyles} ${activeStyles} slider-track-0`
          : 'bg-gray-20 '
      )}
    />
  );
};
const SingleSlider = ({
  disabled = false,
  minVal = 0,
  maxVal = 100,
  defaultValue = maxVal,
  showNumberInput = true,
  liveUpdateValue = false,
  handleChange,
}: IProps) => {
  const [value, setValue] = useState(defaultValue);
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);
  return (
    <div
      aria-disabled={disabled}
      className={clsx('flex items-center gap-4', disabled && 'opacity-30')}
    >
      <div className="flex flex-1 gap-2">
        <Typography variant="body1">{`${minVal}%`}</Typography>
        <div className="group w-full">
          <ReactSlider
            pearling
            ariaLabel={['Thumb']}
            ariaValuetext={(state) => `${state.valueNow}`}
            className="flex h-5 items-center [&>.slider-track-0]:focus-within:bg-green-50" // weird selector... but this is meant to apply styling to the slider when the thumb is focused
            disabled={disabled}
            max={maxVal}
            min={minVal}
            minDistance={1}
            renderThumb={renderThumb}
            renderTrack={renderTrack}
            value={value}
            onAfterChange={() => {
              if (!liveUpdateValue) {
                handleChange?.(value);
              }
            }}
            onChange={(e) => {
              setValue(e);
              if (liveUpdateValue) {
                handleChange?.(e);
              }
            }}
          />
        </div>
        <Typography variant="body1">{`${maxVal}%`}</Typography>
      </div>
      {showNumberInput && (
        <NumberInput
          additionalProps={{ suffix: '%' }}
          className="h-[32px] w-[64px] border-1 border-b-gray-60"
          dataTestId="slider-number-input"
          disabled={disabled}
          value={value.toString()}
          onChange={(e) => {
            const newVal = parseInt(e.target.value, 10);
            if (newVal && newVal <= maxVal) {
              setValue(newVal);
              handleChange?.(newVal);
            }
          }}
        />
      )}
    </div>
  );
};

export default SingleSlider;
